import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuantityInput from './QuantityInput';
import { clsx } from 'clsx';
import {
  CartItemButtons,
  MoveToToggleMenu,
  fToCurrency,
  useParameters,
} from '@web/utils';
import { isSingleItemPerDeliveryEnabled } from '@web/utils/src/isSingleItemPerDeliveryEnabled';
import { OptionType } from '@web/utils/src/components/CartItemButtonts/CartItemButtons';
import { UserSilo } from '@/hooks/useUserSilosAndAddresses';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { DeliveryType } from '@/types';
import { CartDelivery, CartItemData } from '@agrantis/cart';
import {
  moveCartItem,
  removeCartItem,
  updateCartDelivery,
} from '@agrantis/cart/actions/cart';
import { getCommonLocationIds } from '@agrantis/cart/locations';
import {
  CalculatedCartItem,
  selectComputedCartByKey,
} from '@agrantis/cart/selectors';

interface SingleDelieryItemProps {
  item: CalculatedCartItem;
  delivery: CartDelivery;
  silos: UserSilo[];
}

const SingleDeliveryItem = (props: SingleDelieryItemProps) => {
  const { item, delivery, silos } = props;
  const [toggleMenu, setToggleMenu] = useState(false);
  const { retailerId } = useParameters<{ retailerId: string }>();
  const cartKey = retailerId;
  const cart = useAppSelector(selectComputedCartByKey(cartKey));
  const retailer = useAppSelector((state) => state.retailer.retailer);
  const singleItemPerDelivery = isSingleItemPerDeliveryEnabled(
    retailer?.settings?.erpType,
  );
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const moveToNewDelivery = (deliveryId: string) => {
    dispatch(
      moveCartItem({
        cartKey,
        itemId: item.itemData.id,
        deliveryId: delivery.id,
        newDeliveryId: deliveryId,
        singleItemPerDelivery,
      }),
    );
  };

  const moveToDifferentDelivery = () => {
    dispatch(
      moveCartItem({
        cartKey,
        itemId: item.itemData.id,
        deliveryId: delivery.id,
        newDelivery: {
          type: DeliveryType.Delivery,
        },
        singleItemPerDelivery,
      }),
    );
  };

  const moveToPickup = () => {
    if (Object.keys(delivery.items).length === 1) {
      dispatch(
        updateCartDelivery({
          cartKey: cartKey,
          deliveryId: delivery.id,
          type: DeliveryType.Pickup,
        }),
      );
    } else {
      dispatch(
        moveCartItem({
          cartKey: cartKey,
          itemId: item.itemData.id,
          deliveryId: delivery.id,
          newDelivery: {
            type: DeliveryType.Pickup,
          },
          singleItemPerDelivery,
        }),
      );
    }
  };

  const moveToSilo = () => {
    if (Object.keys(delivery.items).length === 1) {
      dispatch(
        updateCartDelivery({
          cartKey,
          deliveryId: delivery.id,
          siloId: silos[0].id,
          deliveryAddress: silos[0].address ?? undefined,
        }),
      );
    } else {
      dispatch(
        moveCartItem({
          cartKey,
          itemId: item.itemData.id,
          deliveryId: delivery.id,
          newDelivery: {
            type: DeliveryType.Delivery,
            siloId: silos[0].id,
            deliveryAddress: silos[0].address,
          },
          singleItemPerDelivery,
        }),
      );
    }
    setToggleMenu(false);
  };

  const deleteItem = () => {
    dispatch(
      removeCartItem({
        cartKey,
        itemId: item.itemData.id,
        deliveryId: delivery.id,
      }),
    );
  };

  const isDeliveryEligible = (
    delivery: CartDelivery,
    itemData: CartItemData,
  ) => {
    const commonLocationIds = getCommonLocationIds([
      itemData.locations.map((loc) => loc.id),
    ]);

    return (
      delivery.locationId &&
      !delivery.siloId &&
      commonLocationIds.has(delivery.locationId) &&
      delivery.type === DeliveryType.Delivery
    );
  };

  const eligibleDeliveries = (singleItemPerDelivery
    ? []
    : Object.values(cart?.deliveries || {}))
    .map((d, i): [d: CartDelivery, i: number] => {
      return [d, i];
    })
    .filter(
      ([d]) =>
        d.id !== delivery.id && // delivery can't be current delivery
        !d.items[item.itemData.id] && // item can't already be in the delivery
        isDeliveryEligible(d, item.itemData), //delivery needs to be eligible
    );

  const deliveriesOptions = useMemo(() => {
    return eligibleDeliveries.map(([delivery, i]) => {
      let labelKey: string;

      switch (delivery.type) {
        case DeliveryType.Delivery:
          labelKey = 'order.delivery';
          break;
        case DeliveryType.Pickup:
          labelKey = 'order.pickup';
          break;
        default:
          labelKey = '';
          break;
      }

      return {
        label: `${t(labelKey as any)} #${i + 1}`,
        value: delivery.id,
      };
    });
  }, [eligibleDeliveries, t]);

  const onOptionSelect = (option: OptionType | string) => {
    if (!retailerId) return;

    switch (option) {
      case OptionType.edit:
        setToggleMenu(true);
        break;
      case OptionType.moveToSilo:
        moveToSilo();
        break;
      case OptionType.delete:
        deleteItem();
        break;
      case OptionType.moveToDelivery:
        moveToDifferentDelivery();
        break;
      case OptionType.moveToPickup:
        moveToPickup();
        break;
      default:
        moveToDifferentDelivery();
        break;
    }
  };

  return (
    <div
      key={item.itemData.id}
      className="py-3-0 md:py-1-0 border-b last:border-0 border-secondary-grey grid grid-cols-[1fr_1fr_1fr_1fr] items-center"
    >
      <div className="flex items-center mb-2-0 md:mb-0 pr-3-0 md:pr-0 w-full">
        <p className="text-2-0 md:text-1-4 font-bd md:max-w-[250px] w-full">
          {item.itemData.articleName}
        </p>
      </div>

      <div className="md:w-1/3">
        <QuantityInput item={item} delivery={delivery} noSuggestions />
      </div>
      <div className="ml-auto pr-10-0 flex flex-col">
        <span
          className={clsx('text-body1 font-bd', {
            'line-through': item.lineDiscountedTotalNet,
          })}
        >
          {!item.price ? '*' : fToCurrency(item.lineTotalNet || 0)}
        </span>
        <span className="text-body1 font-bd">
          {item.lineDiscountedTotalNet &&
            fToCurrency(item.lineDiscountedTotalNet)}
        </span>
      </div>
      <div className="right-0-5 top-1-5 flex flex-col px-1-0 cursor-pointer">
        <CartItemButtons
          silos={silos}
          onOptionSelect={onOptionSelect}
          isSilo={!!delivery.siloId}
          t={t}
        />
        <MoveToToggleMenu
          isOpen={toggleMenu}
          setIsOpened={setToggleMenu}
          eligibleDeliveries={deliveriesOptions}
          moveToNewDelivery={moveToNewDelivery}
          moveToDifferentDelivery={moveToDifferentDelivery}
          moveToPickup={moveToPickup}
          t={t}
          deliveryType={
            delivery.type === DeliveryType.Pickup ? 'Pickup' : 'Delivery'
          }
        />
      </div>
    </div>
  );
};

export default SingleDeliveryItem;
